import { ShipperBid } from '@/hooks/useShipperBids';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Mail, Phone, Package, MapPin, Calendar, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BidCardProps {
  bid: ShipperBid;
  onAccept: () => void;
  onDecline: () => void;
  isAccepting: boolean;
  isDeclining: boolean;
}

export const BidCard = ({ bid, onAccept, onDecline, isAccepting, isDeclining }: BidCardProps) => {
  const { t, i18n } = useTranslation();

  return (
    <Card className="rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border-2 border-[#DBE2EA]">
      <CardContent className="p-6 space-y-4">
        {/* Order Info */}
        <div className="pb-4 border-b border-[#DBE2EA]">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Package className="text-[#0880AE]" size={16} />
                <span className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                  {t('shipper.bids.order')}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-[#37699E] font-heading">
                {bid.order.cargo_title || bid.order.cargo_description}
              </h3>
              <p className="text-sm text-[#5A6B7A]">{bid.order.order_number}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide mb-1">
                {t('shipper.bids.pickup')}
              </p>
              <div className="flex items-start gap-1">
                <MapPin className="text-[#0880AE] flex-shrink-0 mt-0.5" size={14} />
                <span className="text-[#2D3748]">{bid.order.pickup_address}</span>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide mb-1">
                {t('shipper.bids.delivery')}
              </p>
              <div className="flex items-start gap-1">
                <MapPin className="text-[#FF7171] flex-shrink-0 mt-0.5" size={14} />
                <span className="text-[#2D3748]">{bid.order.delivery_address}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Transporter Info */}
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide mb-2">
              {t('shipper.bids.transporter')}
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Building2 className="text-[#37699E]" size={16} />
                <span className="text-sm font-medium text-[#2D3748]">{bid.transporter_company.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="text-[#5A6B7A]" size={14} />
                <span className="text-xs text-[#5A6B7A]">{bid.transporter_company.email}</span>
              </div>
              {bid.transporter_company.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="text-[#5A6B7A]" size={14} />
                  <span className="text-xs text-[#5A6B7A]">{bid.transporter_company.phone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Bid Amount */}
          <div className="p-4 rounded-lg bg-[#0880AE]/10 border border-[#0880AE]/20">
            <p className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide mb-1">
              {t('shipper.bids.proposedAmount')}
            </p>
            <p className="text-3xl font-bold text-[#0880AE] font-heading">
              {bid.amount} €
            </p>
          </div>

          {/* Message */}
          {bid.message && (
            <div className="p-3 rounded-lg bg-[#EBF4F8] border border-[#DBE2EA]">
              <div className="flex items-start gap-2">
                <MessageSquare className="text-[#37699E] flex-shrink-0 mt-0.5" size={16} />
                <div className="flex-1">
                  <p className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide mb-1">
                    {t('shipper.bids.message')}
                  </p>
                  <p className="text-sm text-[#2D3748]">{bid.message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Submitted date */}
          <div className="flex items-center gap-2 text-xs text-[#5A6B7A]">
            <Calendar size={14} />
            <span>
              {t('shipper.bids.submittedOn')} {new Date(bid.created_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : i18n.language === 'es' ? 'es-ES' : i18n.language === 'de' ? 'de-DE' : i18n.language === 'it' ? 'it-IT' : 'fr-FR')}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-[#DBE2EA]">
          <Button
            onClick={onDecline}
            disabled={isDeclining || isAccepting}
            variant="outline"
            className="flex-1 border-[#FF7171] text-[#FF7171] hover:bg-[#FF7171]/10 rounded-lg"
          >
            {isDeclining ? t('common.declining') : t('common.decline')}
          </Button>
          <Button
            onClick={onAccept}
            disabled={isAccepting || isDeclining}
            className="flex-1 bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg"
          >
            {isAccepting ? t('common.accepting') : t('common.accept')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
